import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardFooter,
  Col,
  Container,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select, { OnChangeValue } from "react-select";
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";
import { H5 } from "../../../AbstractElements";
import { SMSData } from "./SMS";
import { customReactSelectStyles } from "../../../styles/ReactSelectStyles";
import ButtonSection from "../../organization/create-organization/ButtonSection";


interface SelectOption {
  value: string;
  label: string;
}


const methodOptions: SelectOption[] = [
  { value: "Sandbox", label: "Sandbox" },
  { value: "Live", label: "Live" },
];

const statusOptions: SelectOption[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const formatOptions: SelectOption[] = [
  { value: "JSON", label: "JSON" },
  { value: "XML", label: "XML" },
];


interface SmsGateway {
  sms_gateway_id: number;
  sms_gateway_name: string;
  provider_name: string;
  type: string;
  sms_gateway_url: string;
  sms_gateway_request_format: string;
  sms_gateway_signup_url: string;
  sms_gateway_recharge_url: string;
  status: string;
  Method: string;
}


const validationSchema = Yup.object().shape({
  provider_name: Yup.string().required("Provider Name is required"),
  sms_gateway_url: Yup.string()
    .url("Must be a valid URL")
    .required("API URL is required"),
  type: Yup.string().required("Gateway Type is required"),
});

function EditSmsContainer() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState<SmsGateway | null>(null);

  useEffect(() => {
    const gatewayToEdit = SMSData.find(
      (gateway: any) => gateway.sms_gateway_id === parseInt(id || "0")
    );
    if (gatewayToEdit) {
      setInitialValues(gatewayToEdit);
    }
  }, [id]);

  const handleSubmit = (values: SmsGateway) => {
    console.log("Saving Formik Data:", values);
    alert("SMS Gateway saved successfully! Check the console.");
    navigate(`${process.env.PUBLIC_URL}/masterSettings/smsgateway`);
  };

  if (!initialValues) {
    return (
      <div className="page-body">
        <Breadcrumbs mainTitle={t("Loading...")} />
        <Container fluid>
          <Card className="mt-4 text-center p-5">
            <H5>Loading or not found...</H5>
          </Card>
        </Container>
      </div>
    );
  }
  const handleClose = () => {
    navigate("/mastersettings/smsgateway");
  };

  return (
    <>
      <Breadcrumbs
        mainTitle={t("Edit SMS Gateway")}
        parents={[
          { label: "MasterSettings" },
          {
            label: t("SMSGateway"),
            link: `${process.env.PUBLIC_URL}/mastersettings/smsgateway`,
          },
        ]}
      />
      <Container fluid>
        <Row>
          <Col xl="12">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {(
                { values, setFieldValue }
              ) => (
                <Form className="theme-form">
                  <Card>
                    <CardBody>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="sms_gateway_name">
                              {t("GatewayName")}
                            </Label>
                            <Field
                              name="sms_gateway_name"
                              type="text"
                              className="form-control"
                              disabled
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="provider_name">
                              {t("ProviderName")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              name="provider_name"
                              type="text"
                              className="form-control"
                            />
                            <ErrorMessage
                              name="provider_name"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="sms_gateway_url">
                              {t("APIURL")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              name="sms_gateway_url"
                              type="url"
                              className="form-control"
                            />
                            <ErrorMessage
                              name="sms_gateway_url"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="type">
                              {t("GatewayType")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              name="type"
                              type="text"
                              className="form-control"
                            />
                            <ErrorMessage
                              name="type"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                        </Col>

                    
                        <Col md={4}>
                          <FormGroup>
                            <Label for="Method">{t("Mode")}</Label>
                            <Select
                              options={methodOptions}
                              styles={customReactSelectStyles}
                              classNamePrefix="select"
                              isClearable={true}
                           
                              value={methodOptions.find(
                                (option) => option.value === values.Method
                              )}
                         
                              onChange={(
                                option: OnChangeValue<SelectOption, false>
                              ) => {
                                setFieldValue(
                                  "Method",
                                  option ? option.value : ""
                                );
                              }}
                              formatOptionLabel={(option: SelectOption) => (
                                <div className="d-flex align-items-center gap-2">
                                  <span>{option.label}</span>
                                </div>
                              )}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label for="status">{t("Status")}</Label>
                            <Select
                              options={statusOptions}
                              styles={customReactSelectStyles}
                              classNamePrefix="select"
                              value={statusOptions.find(
                                (option) => option.value === values.status
                              )}
                              onChange={(
                                option: OnChangeValue<SelectOption, false>
                              ) => {
                                setFieldValue(
                                  "status",
                                  option ? option.value : ""
                                );
                              }}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={4}>
                          <FormGroup>
                            <Label for="sms_gateway_request_format">
                              {t("RequestFormat")}
                            </Label>
                            <Select
                              options={formatOptions}
                              styles={customReactSelectStyles}
                              classNamePrefix="select"
                              value={formatOptions.find(
                                (option) =>
                                  option.value ===
                                  values.sms_gateway_request_format
                              )}
                              onChange={(
                                option: OnChangeValue<SelectOption, false>
                              ) => {
                                setFieldValue(
                                  "sms_gateway_request_format",
                                  option ? option.value : ""
                                );
                              }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                    <CardFooter className="text-end border-0 p-3">
                      <ButtonSection onClose={handleClose} />
                    </CardFooter>
                  </Card>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default EditSmsContainer;
